import React from 'react';
import { Gift, Save } from 'lucide-react';

export default function RewardsTab({ 
  rewardSettings, 
  setRewardSettings, 
  handleRewardSettingsUpdate 
}) {
  return (
    <div data-testid="rewards-tab">
      <h2 className="font-serif text-2xl font-semibold text-[#1a3a5c] mb-6">Reward Points Settings</h2>
      
      <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
        <form onSubmit={handleRewardSettingsUpdate} className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 className="font-medium text-gray-900">Enable Reward Points</h3>
              <p className="text-sm text-gray-500">Allow customers to earn and redeem points</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={rewardSettings.is_enabled}
                onChange={(e) => setRewardSettings({ ...rewardSettings, is_enabled: e.target.checked })}
                className="sr-only peer"
                data-testid="reward-enabled-toggle"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1a3a5c]"></div>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Points Earned per ₹1 Spent
              </label>
              <input
                type="number"
                step="0.1"
                value={rewardSettings.points_per_rupee}
                onChange={(e) => setRewardSettings({ ...rewardSettings, points_per_rupee: parseFloat(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                data-testid="points-per-rupee-input"
              />
              <p className="text-xs text-gray-500 mt-1">Example: 1 = Earn 1 point for every ₹1 spent</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Points to Redeem
              </label>
              <input
                type="number"
                value={rewardSettings.min_points_to_redeem}
                onChange={(e) => setRewardSettings({ ...rewardSettings, min_points_to_redeem: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                data-testid="min-points-input"
              />
              <p className="text-xs text-gray-500 mt-1">Customers must have at least this many points to redeem</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Point Value (₹ per point)
              </label>
              <input
                type="number"
                step="0.01"
                value={rewardSettings.points_value_in_rupees}
                onChange={(e) => setRewardSettings({ ...rewardSettings, points_value_in_rupees: parseFloat(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                data-testid="points-value-input"
              />
              <p className="text-xs text-gray-500 mt-1">Example: 0.25 = 100 points = ₹25 discount</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Redemption (% of order)
              </label>
              <input
                type="number"
                step="1"
                value={rewardSettings.max_redeem_percent}
                onChange={(e) => setRewardSettings({ ...rewardSettings, max_redeem_percent: parseFloat(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                data-testid="max-redeem-input"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum % of order value that can be paid with points</p>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-[#1a3a5c] text-white px-8 py-3 rounded-full hover:bg-[#0d2240] flex items-center space-x-2"
              data-testid="save-reward-settings-button"
            >
              <Save className="h-5 w-5" />
              <span>Save Reward Settings</span>
            </button>
          </div>
        </form>
      </div>

      <div className="bg-[#C9A967]/10 p-6 rounded-lg border border-[#C9A967]/30">
        <h3 className="font-serif text-xl font-semibold text-[#1a3a5c] mb-4 flex items-center">
          <Gift className="h-6 w-6 mr-2" />
          How Reward Points Work
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-semibold text-[#1a3a5c] mb-2">Earning Points</h4>
            <p className="text-gray-600">
              Customers earn {rewardSettings.points_per_rupee} point(s) for every ₹1 spent on successful orders.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-[#1a3a5c] mb-2">Redeeming Points</h4>
            <p className="text-gray-600">
              Minimum {rewardSettings.min_points_to_redeem} points required. Each point = ₹{rewardSettings.points_value_in_rupees}
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-[#1a3a5c] mb-2">Limits</h4>
            <p className="text-gray-600">
              Max {rewardSettings.max_redeem_percent}% of order value can be paid with points.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
